// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Wiring. This file owns no analysis: it moves bytes from a drop zone to the
 * right extractor, hands the results to the worker, and renders what comes back.
 */

import './styles/main.css';
import { ABOUT, HOW_IT_WORKS, PRIVACY } from './content.ts';
import { EventLog } from './eventlog.ts';
import { initGlossary } from './glossary.ts';
import { createModal } from './modal.ts';
import {
  ONLINE_STATE_OFF,
  ONLINE_STATE_ON,
  SHELL,
  archiveEntriesToKeep,
  formatBytes,
  isArchiveName,
  isDraftName,
  isEditableTarget,
  isJunkPath,
  isSourceName,
  renderCards,
  renderPreflight,
  renderResults,
  renderTally,
} from './ui.ts';
import { extractPdf } from './core/pdf.ts';
import { extractQuotes } from './core/quotes.ts';
import { extractIdentifiers, resolveIdentifiers } from './core/identifiers.ts';
import { buildCsv, buildReport } from './core/exports.ts';
import { markupToText } from './core/markup.ts';
import type { DraftQuote, ExtractedSource, IdentifierResult, QuoteResult } from './types.ts';
import type { FromWorker, ToWorker } from './worker.ts';

const app = document.getElementById('app') as HTMLElement;
app.innerHTML = SHELL;
initGlossary();

const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });

const modals = {
  how: createModal('modal-how', 'How it works', HOW_IT_WORKS),
  privacy: createModal('modal-privacy', 'Privacy', PRIVACY),
  about: createModal('modal-about', 'About', ABOUT),
};

const log = new EventLog(
  app,
  document.getElementById('log-toggle') as HTMLButtonElement,
  document.getElementById('log-badge') as HTMLElement,
);

const el = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const runButton = el<HTMLButtonElement>('run');
const sampleButton = el<HTMLButtonElement>('sample');
const onlineToggle = el<HTMLInputElement>('online');
const progress = el<HTMLElement>('progress');
const progressBar = el<HTMLElement>('progress-bar');
const progressFill = el<HTMLElement>('progress-fill');
const progressLabel = el<HTMLElement>('progress-label');
const errorBox = el<HTMLElement>('error');
const resultsBox = el<HTMLElement>('results');
const statusBox = el<HTMLElement>('status');

interface DraftFile {
  name: string;
  text: string;
}

const state: {
  draft: DraftFile | null;
  sourceFiles: File[];
  sources: ExtractedSource[];
  quotes: DraftQuote[];
  results: QuoteResult[];
  identifiers: IdentifierResult[];
  nearCeiling: number;
  busy: boolean;
} = {
  draft: null,
  sourceFiles: [],
  sources: [],
  quotes: [],
  results: [],
  identifiers: [],
  nearCeiling: 0.1,
  busy: false,
};

// ── modal + shell wiring ─────────────────────────────────────────────────────
/*
 * `click`, not `pointerdown`.
 *
 * These are real <button>s, and keyboard activation of a button dispatches
 * only `click` — PointerEvents come from a pointing device. A pointerdown-only
 * opener therefore made the entire threat-model surface (How it works,
 * Privacy, About, and the banner's "what that does and does not cover")
 * unreachable without a mouse: the button took focus, showed its ring, and did
 * nothing. WCAG 2.1.1, Level A.
 *
 * ONE event family per control, never both: a control wired to pointerdown AND
 * click fires twice on a real tap, and an overlay that closes and instantly
 * reopens reads as a dead button while a scripted .click() sees nothing wrong.
 *
 * The old handler also called preventDefault(), which suppressed focus on
 * mouse-down, so modal.ts captured <body> as the element to restore focus to
 * and Escape dumped the user at the top of the document. Not calling it is
 * what returns focus to the trigger.
 */
document.addEventListener('click', (event) => {
  const button = (event.target as HTMLElement).closest<HTMLElement>('[data-modal]');
  if (!button) return;
  const key = button.dataset.modal as keyof typeof modals;
  modals[key]?.open();
});

/** The one channel that announces to a screen reader. Milestones only. */
function announce(message: string): void {
  statusBox.textContent = message;
}

function showError(message: string): void {
  // hidden first, then the text: #error is role="alert", and a live region
  // whose content changes while it is still display:none announces nothing.
  errorBox.hidden = false;
  errorBox.textContent = message;
  log.add('bad', message);
}

function clearError(): void {
  errorBox.hidden = true;
  errorBox.textContent = '';
}

function setProgress(label: string, done: number, total: number): void {
  progress.hidden = false;
  progressLabel.textContent = label;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  progressFill.style.width = `${pct}%`;
  // Exposed for anyone who queries the bar; NOT announced on every tick, which
  // for a 400-page corpus would be hundreds of interruptions.
  progressBar.setAttribute('aria-valuenow', String(pct));
}

function endProgress(): void {
  progress.hidden = true;
  progressFill.style.width = '0%';
  progressBar.setAttribute('aria-valuenow', '0');
}

function renderLists(): void {
  const draftList = el<HTMLElement>('draft-list');
  draftList.innerHTML = state.draft
    ? `<li><span class="tick">✓</span>${escape(state.draft.name)}<span class="quiet"> · ${state.draft.text.length.toLocaleString()} characters</span></li>`
    : '';
  const sourceList = el<HTMLElement>('source-list');
  sourceList.innerHTML = state.sourceFiles
    .map((f) => `<li><span class="tick">✓</span>${escape(f.name)}<span class="quiet"> · ${formatBytes(f.size)}</span></li>`)
    .join('');
  runButton.disabled = state.busy || !state.draft || state.sourceFiles.length === 0;
}

function escape(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ── file intake ──────────────────────────────────────────────────────────────
/*
 * Folders and archives.
 *
 * "Drag the whole folder of them here" is the sources card's primary
 * instruction, and it used to be impossible: the drop handler read only
 * `dataTransfer.files`, which for a directory drop yields one extension-less
 * File that the format test then rejected — silently, because nothing on this
 * path called showError(). The audience for this tool is a person with a
 * folder of PDFs, so the promise is implemented rather than withdrawn.
 *
 * Three caps, because a dropped folder is unbounded: a file count, a recursion
 * depth, and the junk filter in ui.ts. All three announce themselves in the
 * log rather than trimming quietly.
 */
const MAX_DROPPED_FILES = 1500;
const MAX_DIR_DEPTH = 12;

function entryFile(entry: FileSystemFileEntry): Promise<File | null> {
  return new Promise((resolve) => entry.file((file) => resolve(file), () => resolve(null)));
}

function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  // readEntries() hands back at most 100 entries per call and signals the end
  // of the directory with an EMPTY batch, so it has to be drained in a loop.
  // Reading it once silently truncates a folder of 240 papers to 100 — and the
  // missing 140 would then read as sources the user simply does not hold.
  return new Promise((resolve) => {
    const all: FileSystemEntry[] = [];
    const pump = (): void => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(all);
          return;
        }
        all.push(...batch);
        pump();
      }, () => resolve(all));
    };
    pump();
  });
}

async function walkEntry(entry: FileSystemEntry, out: File[], depth: number): Promise<void> {
  if (out.length >= MAX_DROPPED_FILES) return;
  if (isJunkPath(entry.fullPath || entry.name)) return;
  if (entry.isFile) {
    const file = await entryFile(entry as FileSystemFileEntry);
    if (file) out.push(file);
    return;
  }
  if (!entry.isDirectory || depth >= MAX_DIR_DEPTH) return;
  const children = await readAllEntries((entry as FileSystemDirectoryEntry).createReader());
  for (const child of children) await walkEntry(child, out, depth + 1);
}

/**
 * Expand any dropped .zip through the same route the bundled sample takes —
 * one ingestion path, so the sample cannot pass while the real one is broken.
 */
async function expandArchives(files: File[]): Promise<File[]> {
  if (!files.some((f) => isArchiveName(f.name))) return files;
  const { unzipSync } = await import('fflate');
  const out: File[] = [];
  for (const file of files) {
    if (!isArchiveName(file.name)) {
      out.push(file);
      continue;
    }
    try {
      const archive = unzipSync(new Uint8Array(await file.arrayBuffer()));
      const keep = archiveEntriesToKeep(Object.keys(archive));
      for (const name of keep) {
        out.push(new File([archive[name] as BlobPart], name.split('/').pop() ?? name));
      }
      log.add(
        keep.length > 0 ? 'good' : 'warn',
        `${file.name}: ${keep.length} usable file${keep.length === 1 ? '' : 's'} inside the archive.`,
      );
    } catch (error) {
      log.add('bad', `${file.name}: could not be opened as a zip — ${(error as Error).message}`);
    }
  }
  return out;
}

/**
 * Turn a drop into a flat list of files.
 *
 * `entries` MUST have been collected synchronously in the drop handler: the
 * DataTransferItemList is neutered the moment that handler returns, so a
 * single `await` before calling webkitGetAsEntry() yields an empty list and
 * the folder appears to contain nothing.
 */
async function collectDrop(entries: (FileSystemEntry | null)[], plain: File[]): Promise<File[]> {
  const walked: File[] = [];
  for (const entry of entries) {
    if (entry) await walkEntry(entry, walked, 0);
  }
  // webkitGetAsEntry() is absent on older WebKit and on some Linux builds, so
  // dataTransfer.files remains the fallback — it just cannot see inside a
  // folder.
  const files = walked.length > 0 ? walked : plain.filter((f) => !isJunkPath(f.name));
  if (walked.length >= MAX_DROPPED_FILES) {
    log.add('warn', `That folder holds more than ${MAX_DROPPED_FILES} files; I stopped there. Drop it in parts if you need all of them.`);
  }
  return expandArchives(files);
}

async function setDraft(file: File): Promise<void> {
  if (/\.docx$/i.test(file.name)) {
    const source = await parseInWorker({
      type: 'parse-docx',
      id: -1,
      name: file.name,
      data: await file.arrayBuffer(),
    });
    state.draft = { name: file.name, text: source.text };
  } else {
    state.draft = { name: file.name, text: await file.text() };
  }
  log.add('good', `Draft loaded: ${file.name} (${state.draft.text.length.toLocaleString()} characters).`);
  announce(`Draft added: ${file.name}, ${state.draft.text.length.toLocaleString()} characters.`);
  renderLists();
}

function addSources(files: File[]): void {
  const accepted = files.filter((f) => isSourceName(f.name));
  const rejected = files.length - accepted.length;
  // A drop that yields nothing usable used to be reported ONLY as a log line
  // in a drawer that is closed by default — level 'good', reading "0 source
  // files added." beside a green tick. Silence is the actual defect here: the
  // sibling draft zone has always shown an error on a rejected drop.
  if (accepted.length === 0) {
    showError(
      files.length === 0
        ? 'Nothing usable arrived in that drop. Drop the files themselves, or the folder that holds them — a link or an image of a page has nothing in it for me to read.'
        : `None of those ${files.length} file${files.length === 1 ? ' is a format' : 's are formats'} I can read. I can read .pdf, .docx, .epub, .txt, .md and .html — and a .zip or a folder of any of those.`,
    );
    return;
  }
  const before = state.sourceFiles.length;
  const existing = new Set(state.sourceFiles.map((f) => `${f.name}:${f.size}`));
  for (const file of accepted) {
    const key = `${file.name}:${file.size}`;
    if (existing.has(key)) continue;
    existing.add(key);
    state.sourceFiles.push(file);
  }
  const added = state.sourceFiles.length - before;
  log.add('good', `${added} source file${added === 1 ? '' : 's'} added.`);
  if (rejected > 0) log.add('warn', `${rejected} file${rejected === 1 ? '' : 's'} ignored — unsupported format.`);
  announce(
    `${added} source file${added === 1 ? '' : 's'} added, ${state.sourceFiles.length} in total.` +
      (rejected > 0 ? ` ${rejected} ignored as an unsupported format.` : ''),
  );
  renderLists();
}

interface DropWiring {
  zoneId: string;
  inputId: string;
  /** The real, focusable control inside the zone. */
  pickId: string;
  /** A second control and input for directory selection, where supported. */
  folder?: { pickId: string; inputId: string };
  onFiles: (files: File[]) => void;
}

function wireDrop({ zoneId, inputId, pickId, folder, onFiles }: DropWiring): void {
  const zone = el<HTMLElement>(zoneId);
  const input = el<HTMLInputElement>(inputId);
  const pick = el<HTMLButtonElement>(pickId);

  const openPicker = (target: HTMLInputElement) => () => target.click();
  pick.addEventListener('click', openPicker(input));

  // Clicking the card is a convenience on top of the button, so it must not
  // also fire when the click was ON one of the buttons — that would open two
  // pickers. (The nested <input>'s own click() re-enters here, which is
  // harmless: HTMLElement.click() returns early while a click is in progress.)
  zone.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    if (target.closest('.file-list') || target.closest('.drop-choose')) return;
    input.click();
  });

  const wireInput = (element: HTMLInputElement) => {
    element.addEventListener('change', () => {
      const files = [...(element.files ?? [])].filter((f) => !isJunkPath(f.name));
      element.value = '';
      void expandArchives(files).then(onFiles);
    });
  };
  wireInput(input);

  if (folder) {
    const folderInput = el<HTMLInputElement>(folder.inputId);
    const folderPick = el<HTMLButtonElement>(folder.pickId);
    // webkitdirectory and multi-file selection cannot live on one input, and
    // the attribute is unsupported on iOS, so the second control only appears
    // where it actually works rather than being a button that does nothing.
    if ('webkitdirectory' in HTMLInputElement.prototype) {
      folderInput.webkitdirectory = true;
      folderPick.hidden = false;
      folderPick.addEventListener('click', openPicker(folderInput));
      wireInput(folderInput);
    }
  }

  for (const type of ['dragenter', 'dragover'] as const) {
    zone.addEventListener(type, (event) => {
      event.preventDefault();
      zone.classList.add('over');
    });
  }
  for (const type of ['dragleave', 'drop'] as const) {
    zone.addEventListener(type, (event) => {
      event.preventDefault();
      zone.classList.remove('over');
    });
  }
  zone.addEventListener('drop', (event) => {
    const transfer = event.dataTransfer;
    if (!transfer) return;
    // Synchronous, before any await — see collectDrop().
    const entries = [...transfer.items].map((item) =>
      typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null,
    );
    const plain = [...transfer.files];
    // Always calls onFiles, including with an empty list: the zero-file case is
    // exactly the one that used to disappear.
    void collectDrop(entries, plain).then(onFiles).catch((error: Error) => {
      showError(`I could not read what you dropped: ${error.message}`);
    });
  });
}

wireDrop({
  zoneId: 'drop-draft',
  inputId: 'draft-input',
  pickId: 'pick-draft',
  onFiles: (files) => {
    const draft = files.find((f) => isDraftName(f.name));
    if (!draft) {
      showError(
        files.length === 0
          ? 'Nothing usable arrived in that drop. Give me a .md, .txt or .docx file.'
          : 'That is not a draft I can read. Give me a .md, .txt or .docx file.',
      );
      return;
    }
    clearError();
    void setDraft(draft).catch((e) => showError(`Could not read the draft: ${(e as Error).message}`));
  },
});

wireDrop({
  zoneId: 'drop-sources',
  inputId: 'source-input',
  pickId: 'pick-sources',
  folder: { pickId: 'pick-source-folder', inputId: 'source-folder-input' },
  onFiles: (files) => {
    clearError();
    addSources(files);
  },
});

// Paste a draft straight in — the fastest path when the draft is in another tab.
document.addEventListener('paste', (event) => {
  // Any editable target is left alone. See isEditableTarget() in ui.ts: the
  // guard used to test `tagName === 'INPUT'` only, which broke every textarea
  // on the page and silently overwrote the loaded draft with what was pasted.
  if (isEditableTarget(event.target)) return;
  const text = event.clipboardData?.getData('text/plain');
  if (!text || text.trim().length < 40) return;
  event.preventDefault();
  state.draft = { name: 'pasted draft', text };
  log.add('good', `Draft pasted (${text.length.toLocaleString()} characters).`);
  announce(`Draft pasted, ${text.length.toLocaleString()} characters. It replaced any draft already loaded.`);
  renderLists();
});

// ── worker plumbing ──────────────────────────────────────────────────────────
/** True only while a near-match boundary change is being re-run. */
let tuning = false;

function setTuneStatus(text: string): void {
  const node = document.getElementById('tune-status');
  if (node) node.textContent = text;
}

let nextTicket = 0;
const pending = new Map<number, { resolve: (s: ExtractedSource) => void; reject: (e: Error) => void }>();
let analyseResolve: ((r: QuoteResult[]) => void) | null = null;
let analyseReject: ((e: Error) => void) | null = null;

worker.onmessage = (event: MessageEvent<FromWorker>) => {
  const message = event.data;
  if (message.type === 'parsed') {
    const ticket = pending.get(message.source.id);
    pending.delete(message.source.id);
    ticket?.resolve(message.source);
  } else if (message.type === 'parse-failed') {
    const ticket = pending.get(message.id);
    pending.delete(message.id);
    ticket?.reject(new Error(message.message));
  } else if (message.type === 'indexed') {
    log.add('info', `Indexed ${message.tokens.toLocaleString()} words across ${message.sources} sources.`);
  } else if (message.type === 'progress') {
    // A boundary change re-runs matching, so these arrive then too — and the
    // page-level bar sits above the results, so showing it would shove the
    // slider down mid-drag. During a re-check the tune panel reports instead.
    if (tuning) setTuneStatus(`Re-checking ${message.done} of ${message.total}…`);
    else setProgress(`Checking quotation ${message.done} of ${message.total}…`, message.done, message.total);
  } else if (message.type === 'analysed') {
    analyseResolve?.(message.results);
    analyseResolve = null;
  } else if (message.type === 'failed') {
    analyseReject?.(new Error(message.message));
    analyseReject = null;
  }
};

function parseInWorker(message: ToWorker & { id: number; name: string }): Promise<ExtractedSource> {
  const id = message.id >= 0 ? message.id : -(++nextTicket);
  const payload = { ...message, id } as ToWorker;
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    worker.postMessage(payload, 'data' in payload && payload.data ? [payload.data as ArrayBuffer] : []);
  });
}

function analyse(sources: ExtractedSource[], quotes: DraftQuote[]): Promise<QuoteResult[]> {
  return new Promise((resolve, reject) => {
    analyseResolve = resolve;
    analyseReject = reject;
    worker.postMessage({ type: 'analyse', sources, quotes, options: { nearCeiling: state.nearCeiling } } as ToWorker);
  });
}

// ── the run ──────────────────────────────────────────────────────────────────
async function extractSource(file: File, id: number): Promise<ExtractedSource> {
  const name = file.name;
  if (/\.pdf$/i.test(name)) {
    // pdf.js stays on the main thread: it already owns a worker, and nesting
    // two was measured 2.3-3.4x slower.
    return extractPdf(id, name, await file.arrayBuffer(), (done, total) => {
      setProgress(`Reading ${name} — page ${done} of ${total}`, done, total);
    });
  }
  if (/\.docx$/i.test(name)) {
    return parseInWorker({ type: 'parse-docx', id, name, data: await file.arrayBuffer() });
  }
  if (/\.epub$/i.test(name)) {
    return parseInWorker({ type: 'parse-epub', id, name, data: await file.arrayBuffer() });
  }
  const raw = await file.text();
  return parseInWorker({ type: 'parse-text', id, name, text: raw, bytes: file.size } as ToWorker & { id: number; name: string });
}

async function run(): Promise<void> {
  if (!state.draft || state.sourceFiles.length === 0 || state.busy) return;
  state.busy = true;
  runButton.disabled = true;
  clearError();
  resultsBox.hidden = true;
  announce(
    `Checking ${state.draft.name} against ${state.sourceFiles.length} source file${state.sourceFiles.length === 1 ? '' : 's'}. This can take a while.`,
  );

  try {
    const started = performance.now();
    state.sources = [];
    for (let i = 0; i < state.sourceFiles.length; i++) {
      const file = state.sourceFiles[i];
      setProgress(`Reading ${file.name} (${i + 1} of ${state.sourceFiles.length})…`, i, state.sourceFiles.length);
      try {
        const source = await extractSource(file, i);
        state.sources.push(source);
        log.add(
          source.unsearchable ? 'warn' : 'good',
          source.unsearchable
            ? `${file.name}: no text layer — it will never be blamed for a missing quotation.`
            : `${file.name}: ${source.text.length.toLocaleString()} characters from ${source.pages.length} page${source.pages.length === 1 ? '' : 's'}.`,
        );
      } catch (error) {
        const message = (error as Error).message;
        state.sources.push({
          id: i,
          name: file.name,
          kind: 'text',
          bytes: file.size,
          text: '',
          pages: [],
          provenance: 'sheet',
          unsearchable: true,
          runningLinesDropped: 0,
          error: message,
          pageNote: '',
        });
        log.add('bad', `${file.name}: could not be read — ${message}`);
      }
    }

    state.quotes = extractQuotes(state.draft.text);
    log.add('info', `Found ${state.quotes.length} quotation${state.quotes.length === 1 ? '' : 's'} in the draft.`);
    if (state.quotes.length === 0) {
      showError(
        'I could not find any quotations in that draft. I look for text inside quotation marks, block quotes and indented quoted passages — an apostrophe on its own is never treated as a quotation mark.',
      );
      return;
    }

    setProgress('Indexing the sources…', 0, 1);
    state.results = await analyse(state.sources, state.quotes);

    state.identifiers = [];
    let sent = 0;
    if (onlineToggle.checked) {
      const identifiers = extractIdentifiers(state.draft.text);
      if (identifiers.length > 0) {
        sent = identifiers.length;
        log.add('warn', `Sending ${identifiers.length} identifier${identifiers.length === 1 ? '' : 's'} to public registries — no other part of your draft or sources is sent.`);
        const controller = new AbortController();
        state.identifiers = await resolveIdentifiers(identifiers, controller.signal, (done, total) => {
          setProgress(`Checking identifier ${done} of ${total}…`, done, total);
        });
        // A registry that could not be reached is a gap in this run, not a
        // finding about the user's citation, and the log is where the run
        // accounts for itself — it said nothing at all about this before.
        const unreachable = state.identifiers.filter((r) => r.verdict === 'not-checked').length;
        if (unreachable > 0) {
          log.add(
            'warn',
            `${unreachable} identifier${unreachable === 1 ? ' was' : 's were'} not checked — I could not reach a registry.`,
          );
        }
      } else {
        log.add('info', 'No DOIs, PMIDs or arXiv ids found in the draft.');
      }
    }

    renderAll();
    const seconds = ((performance.now() - started) / 1000).toFixed(1);
    // "Nothing was uploaded." used to be unconditional, and was posted directly
    // beneath the line saying N identifiers had just been sent. It is scoped to
    // what actually happened on this run.
    log.add(
      'good',
      sent === 0
        ? `Done in ${seconds}s. Nothing was uploaded.`
        : `Done in ${seconds}s. Nothing was uploaded except the ${sent} identifier${sent === 1 ? '' : 's'} you asked me to check.`,
    );
    announce(`Check complete. ${summaryText()}`);
  } catch (error) {
    showError(`Something failed while checking: ${(error as Error).message}`);
  } finally {
    endProgress();
    state.busy = false;
    renderLists();
  }
}

/** A fresh run: build the whole panel and take the reader to it. */
function renderAll(): void {
  resultsBox.innerHTML = renderPreflight(state.sources) + renderResults(state.quotes, state.results, state.sources, state.identifiers);
  resultsBox.hidden = false;
  wireResultActions();
  resultsBox.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/*
 * A boundary change: replace ONLY what the boundary changes.
 *
 * renderAll() used to serve both cases, and its `resultsBox.innerHTML = …`
 * destroyed the <input type="range"> the user was still holding. Measured on
 * the live build: one ArrowRight left document.activeElement === <body>, so a
 * keyboard user got exactly one 1% step per full tab-through of the page; and
 * the scrollIntoView that follows put the slider 358px below the fold on a
 * 1280x812 viewport, with a larger corpus pushing it further. The preflight
 * table, the download buttons, the identifier table and the tune panel are all
 * unaffected by the boundary, so they are left alone — which keeps the
 * slider's node, its focus and the page's scroll position exactly as they were.
 */
function renderTuned(): void {
  el<HTMLElement>('tally').innerHTML = renderTally(state.results);
  el<HTMLElement>('results-list').innerHTML = renderCards(state.quotes, state.results, state.sources);
}

function download(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  // Revoked on the next frame: revoking synchronously races the download in
  // Safari and silently produces a zero-byte file.
  requestAnimationFrame(() => URL.revokeObjectURL(url));
  log.add('good', `${filename} saved.`);
}

function summaryText(): string {
  const verified = state.results.filter((r) => r.cls === 'exact' || r.cls === 'exact-ellipsis').length;
  const attention = state.results.filter((r) => ['near', 'altered', 'elsewhere', 'not-found'].includes(r.cls)).length;
  const unchecked = state.results.filter((r) => r.cls === 'not-checked' || r.cls === 'unreadable').length;
  return `${state.results.length} quotations checked · ${verified} verified · ${attention} need attention · ${unchecked} not checked (quotewitness, run locally)`;
}

function wireResultActions(): void {
  el<HTMLButtonElement>('dl-csv').addEventListener('click', () => {
    download('quote-audit.csv', buildCsv(state.quotes, state.results, state.sources), 'text/csv;charset=utf-8');
  });

  el<HTMLButtonElement>('dl-report').addEventListener('click', () => {
    download(
      'draft-annotated.html',
      buildReport({
        draftName: state.draft?.name ?? 'draft',
        draftText: state.draft?.text ?? '',
        quotes: state.quotes,
        results: state.results,
        sources: state.sources,
        identifiers: state.identifiers,
        generatedAt: new Date().toLocaleString(),
      }),
      'text/html;charset=utf-8',
    );
  });

  el<HTMLButtonElement>('copy-summary').addEventListener('click', () => {
    void navigator.clipboard
      .writeText(summaryText())
      .then(() => log.add('good', 'Summary copied.'))
      .catch(() => log.add('warn', 'The browser refused clipboard access.'));
  });

  const share = el<HTMLButtonElement>('share');
  if (typeof navigator.share === 'function') {
    share.hidden = false;
    share.addEventListener('click', () => {
      const file = new File(
        [
          buildReport({
            draftName: state.draft?.name ?? 'draft',
            draftText: state.draft?.text ?? '',
            quotes: state.quotes,
            results: state.results,
            sources: state.sources,
            identifiers: state.identifiers,
            generatedAt: new Date().toLocaleString(),
          }),
        ],
        'draft-annotated.html',
        { type: 'text/html' },
      );
      const payload: ShareData = navigator.canShare?.({ files: [file] })
        ? { files: [file], title: 'Quotation audit' }
        : { title: 'Quotation audit', text: summaryText() };
      void navigator.share(payload).catch(() => undefined);
    });
  }

  const near = el<HTMLInputElement>('near');
  const nearOut = el<HTMLOutputElement>('near-out');
  near.value = String(Math.round(state.nearCeiling * 100));
  nearOut.textContent = `${near.value}%`;
  near.addEventListener('input', () => {
    nearOut.textContent = `${near.value}%`;
  });
  near.addEventListener('change', () => {
    state.nearCeiling = Number(near.value) / 100;
    // Re-classification is cheap; the sources are already indexed in the
    // worker's memory only for the duration of a run, so this re-runs matching.
    void reclassify();
  });
}

async function reclassify(): Promise<void> {
  if (state.busy || state.sources.length === 0) return;
  state.busy = true;
  tuning = true;
  const list = el<HTMLElement>('results-list');
  try {
    setTuneStatus('Re-checking…');
    list.setAttribute('aria-busy', 'true');
    state.results = await analyse(state.sources, state.quotes);
    renderTuned();
    announce(`Re-checked at ${Math.round(state.nearCeiling * 100)}%. ${summaryText()}`);
  } catch (error) {
    showError(`Could not re-check: ${(error as Error).message}`);
  } finally {
    setTuneStatus('');
    list.setAttribute('aria-busy', 'false');
    tuning = false;
    state.busy = false;
    renderLists();
  }
}

// The switch's own sentence is a readout of it. It used to begin with a
// hardcoded "Off." that survived the user ticking the box, so the privacy
// control asserted a state that its own checkbox contradicted.
onlineToggle.addEventListener('change', () => {
  el<HTMLElement>('online-state').textContent = onlineToggle.checked ? ONLINE_STATE_ON : ONLINE_STATE_OFF;
});

runButton.addEventListener('click', () => void run());

document.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && !runButton.disabled) {
    event.preventDefault();
    void run();
  }
});

// ── the sample ───────────────────────────────────────────────────────────────
sampleButton.addEventListener('click', () => void loadSample());

async function loadSample(): Promise<void> {
  if (state.busy) return;
  sampleButton.disabled = true;
  try {
    clearError();
    setProgress('Fetching the sample…', 0, 2);
    const base = import.meta.env.BASE_URL;
    const [draftResponse, zipResponse] = await Promise.all([
      fetch(`${base}samples/draft-with-quotes.md`),
      fetch(`${base}samples/sources.zip`),
    ]);
    if (!draftResponse.ok || !zipResponse.ok) throw new Error('the sample files could not be fetched');

    state.draft = { name: 'draft-with-quotes.md', text: await draftResponse.text() };
    const { unzipSync } = await import('fflate');
    const archive = unzipSync(new Uint8Array(await zipResponse.arrayBuffer()));

    // The sample goes through EXACTLY the ingestion path a dropped file takes:
    // it becomes a File and joins the same list, and it is filtered by the same
    // archiveEntriesToKeep() that a dropped .zip goes through. There is no
    // second code path for it to work in while the real one is broken.
    state.sourceFiles = archiveEntriesToKeep(Object.keys(archive)).map(
      (name) => new File([archive[name] as BlobPart], name.split('/').pop() ?? name, { type: 'application/pdf' }),
    );

    log.add('good', `Sample loaded: a draft with quotations and ${state.sourceFiles.length} source files.`);
    renderLists();
    endProgress();
    await run();
  } catch (error) {
    endProgress();
    showError(`Could not load the sample: ${(error as Error).message}`);
  } finally {
    sampleButton.disabled = false;
  }
}

// Exposed for the deployment dry-run, which cannot type into a file picker.
// It calls the same functions the interface does, so a pass here is a pass for
// the real path and not for a parallel one.
interface TestHook {
  loadSample: () => Promise<void>;
  state: typeof state;
  extractQuotes: typeof extractQuotes;
  markupToText: typeof markupToText;
}
(window as unknown as { __quotewitness: TestHook }).__quotewitness = {
  loadSample,
  state,
  extractQuotes,
  markupToText,
};

log.add('info', 'Ready. Nothing you drop here is uploaded.');
renderLists();
